import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { getPoi, updatePoi } from '../../api/resources';
import { CopyableId } from '../CopyableText';
import { DetailField, DetailFieldGrid } from './DetailField';
import { KeyFactsSummary } from './facts/KeyFactsSummary';
import { PoiFactsSection, PoiVerificationsSection } from './PoiFactsSection';

export function PoiDetailPanel({ poiId }: { poiId: string }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [editOpen, setEditOpen] = useState(false);
    const [name, setName] = useState('');
    const [claimedAddress, setClaimedAddress] = useState('');
    const [claimedCity, setClaimedCity] = useState('');
    const [claimedState, setClaimedState] = useState('');

    const poiQ = useQuery({
        queryKey: ['poi', poiId],
        queryFn: () => getPoi(poiId),
    });

    const updateMutation = useMutation({
        mutationFn: () =>
            updatePoi(poiId, {
                name: name.trim() || undefined,
                claimedAddress: claimedAddress.trim() || undefined,
                claimedCity: claimedCity.trim() || undefined,
                claimedState: claimedState.trim() || undefined,
            }),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.pois.updated') });
            setEditOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['poi', poiId] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const poi = poiQ.data;
    if (!poi) {
        return <Text c="dimmed">{t('common.loading.poi')}</Text>;
    }

    const empty = t('common.empty');

    const openEdit = () => {
        setName(poi.name ?? '');
        setClaimedAddress(poi.claimedAddress ?? '');
        setClaimedCity(poi.claimedCity ?? '');
        setClaimedState(poi.claimedState ?? '');
        setEditOpen(true);
    };

    return (
        <>
            <Group justify="flex-end" mb="md">
                <Button size="xs" variant="light" onClick={openEdit}>
                    {t('common.actions.edit')}
                </Button>
            </Group>
            <DetailFieldGrid>
            <DetailField label={t('fields.name')} value={poi.name ?? empty} />
            <DetailField label={t('fields.type')} value={poi.poiType ?? empty} />
            <DetailField label={t('fields.description')} value={poi.description ?? empty} />
            <DetailField label={t('fields.address')} value={poi.claimedAddress ?? empty} />
            <DetailField label={t('fields.city')} value={poi.claimedCity ?? empty} />
            <DetailField label={t('fields.state')} value={poi.claimedState ?? empty} />
            <DetailField
                label={t('fields.coordinates')}
                value={
                    poi.claimedLatitude && poi.claimedLongitude
                        ? `${poi.claimedLatitude}, ${poi.claimedLongitude}`
                        : empty
                }
            />
            <DetailField
                label={t('fields.verificationStatus')}
                value={poi.verificationStatus ?? empty}
            />
            <DetailField label={t('fields.poiId')} value={<CopyableId id={poi.id} />} />
            <DetailField
                label={t('fields.created')}
                value={poi.createdAt ? new Date(poi.createdAt).toLocaleString() : empty}
            />
            <DetailField
                label={t('fields.lastVerified')}
                value={poi.lastVerifiedAt ? new Date(poi.lastVerifiedAt).toLocaleString() : empty}
            />
            </DetailFieldGrid>
            <KeyFactsSummary entityKind="poi" facts={poi.facts ?? []} />
            <PoiFactsSection facts={poi.facts ?? []} />
            <PoiVerificationsSection verifications={poi.verifications ?? []} />

            <Modal opened={editOpen} onClose={() => setEditOpen(false)} title={t('features.pois.editTitle')}>
                <Stack gap="md">
                    <TextInput label={t('fields.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
                    <TextInput label={t('fields.address')} value={claimedAddress} onChange={(e) => setClaimedAddress(e.currentTarget.value)} />
                    <TextInput label={t('fields.city')} value={claimedCity} onChange={(e) => setClaimedCity(e.currentTarget.value)} />
                    <TextInput label={t('fields.state')} value={claimedState} onChange={(e) => setClaimedState(e.currentTarget.value)} />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setEditOpen(false)}>{t('common.actions.cancel')}</Button>
                        <Button loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>{t('common.actions.save')}</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}
